import type { ThemeOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    progress: {
      active: string;
      pending: string;
    };
    badge: {
      background: string;
    };
    brand: {
      plum: string;
      violet: string;
      lime: string;
      limeSoft: string;
      line: string;
    };
  }

  interface PaletteOptions {
    progress?: {
      active?: string;
      pending?: string;
    };
    badge?: {
      background?: string;
    };
    brand?: {
      plum?: string;
      violet?: string;
      lime?: string;
      limeSoft?: string;
      line?: string;
    };
  }
}

/**
 * Paleta da marca Identila (rebranding-identila.pdf).
 *
 * Três cores, com papéis distintos — trocar uma pela outra é o erro fácil:
 * - `plum` é SUPERFÍCIE (bloco escuro, header, hero), nunca o botão;
 * - `violet` é AÇÃO (CTA, link, foco, ícone ativo, marca);
 * - `lime` é MARCADOR (realce atrás do texto, onda, destaque sobre escuro).
 */
export const ONBOARDING_COLORS = {
  backgroundDefault: '#F7F7F2',
  textPrimary: '#171918',
  primaryMain: '#8A3FD0',
  plum: '#48204F',
  lime: '#D5F919',
  limeSoft: '#F1FDB4',
  line: '#E4E2DA',
  progressActive: '#D5F919',
  progressPending: '#3D403E',
  /** Marcador: lime cheio com texto tinta — o gesto de marca-texto do manual. */
  badgeBackground: '#D5F919',
} as const;

export const ONBOARDING_FONT_FAMILY = '"Outfit", system-ui, sans-serif';

export const ONBOARDING_CTA_BORDER_RADIUS = 28;

export const ONBOARDING_CTA_HEIGHT = 56;

export const ONBOARDING_BREAKPOINTS = {
  md: 768,
  xl: 1280,
} as const;

export const ONBOARDING_CONTENT_MAX_WIDTH = {
  xs: 390,
  md: 480,
  xl: 520,
} as const;

export const onboardingThemeOptions: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: ONBOARDING_BREAKPOINTS.md,
      lg: 1024,
      xl: ONBOARDING_BREAKPOINTS.xl,
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: ONBOARDING_COLORS.primaryMain,
    },
    // Lime nunca leva texto branco — o contraste é de 1.2:1.
    secondary: {
      main: ONBOARDING_COLORS.lime,
      contrastText: ONBOARDING_COLORS.textPrimary,
    },
    text: {
      primary: ONBOARDING_COLORS.textPrimary,
    },
    background: {
      default: ONBOARDING_COLORS.backgroundDefault,
      paper: '#ffffff',
    },
    progress: {
      active: ONBOARDING_COLORS.progressActive,
      pending: ONBOARDING_COLORS.progressPending,
    },
    badge: {
      background: ONBOARDING_COLORS.badgeBackground,
    },
    brand: {
      plum: ONBOARDING_COLORS.plum,
      violet: ONBOARDING_COLORS.primaryMain,
      lime: ONBOARDING_COLORS.lime,
      limeSoft: ONBOARDING_COLORS.limeSoft,
      line: ONBOARDING_COLORS.line,
    },
  },
  typography: {
    fontFamily: ONBOARDING_FONT_FAMILY,
  },
  shape: {
    borderRadius: ONBOARDING_CTA_BORDER_RADIUS,
  },
};
